import { Instance, flow, getEnv, types, cast } from "mobx-state-tree";

import { UserOrganizationInvitationResponse } from "../../api-models/response/organization/OrganizationDetailsResponse";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { Column, ColumnType } from "../../components/datatable/interfaces";
import { endpoints } from "../api/ApiEndpoints";
import { singleItemPatch } from "../api/ApiService";
import { UserOrganizationInvitation } from "../user/my-account/UserOrganizationInvitation";
import { Option } from "../../components/datatable/interfaces";
import { EmploymentStatusResponse } from "../../api-models/response/EmploymentStatusResponse";
import { Role } from "../user/Role";
import { OrganizationInvitationStatuses } from "../../generated/OrganizationInvitations";

const MyOrganizationInvitationsModel = types.model({
  requestStatusOptions: types.array(types.frozen<Option>()),
  userRoleOptions: types.array(types.frozen<Option>()),
  employmentStatusOptions: types.array(types.frozen<Option>()),
});

export const MyOrganizationInvitationsStorageModel = types
  .compose(DataGridStorageModel, MyOrganizationInvitationsModel)
  .named("MyOrganizationInvitationRequestsStorage")
  .views((self) => ({
    get tableColumns(): Column[] {
      return [
        {
          key: "name",
          name: "organization.DisplayOrganizationName",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
          flex: 2,
        },
        {
          key: "organizationInvitationStatus",
          name: "organization.DisplayInvitationStatus",
          type: ColumnType.SingleSelect,
          sortable: true,
          filterable: true,
          valueOptions: self.requestStatusOptions,
        },
        {
          key: "created",
          name: "organization.DisplayInvitationDate",
          type: ColumnType.DateTime,
          sortable: true,
          filterable: true,
        },
        {
          key: "contact",
          name: "organization.DisplayOrganizationContactName",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "email",
          name: "organization.DisplayOrganizationEmail",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "phone",
          name: "organization.organization-phone",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "employmentStatus",
          name: "organization.DisplayOrganizationEmploymentStatus",
          type: ColumnType.SingleSelect,
          sortable: true,
          filterable: true,
          valueOptions: self.employmentStatusOptions,
        },
        {
          key: "role",
          name: "organization.DisplayOrganizationEmploymentRole",
          type: ColumnType.SingleSelect,
          sortable: true,
          filterable: true,
          valueOptions: self.userRoleOptions,
        },
      ];
    },
  }))
  .actions((self) => {
    function initializeInvitationStatusOptions() {
      const requestStatusesOptions: Option[] = Object.entries(OrganizationInvitationStatuses)
        .filter((s) => typeof s[1] === "number")
        .map((s) => ({
          label: `api.organization.invitation.status.${s[0]}`,
          value: s[1].toString(),
        }));

      self.requestStatusOptions = cast(requestStatusesOptions);
    }

    return {
      mapItem(item: UserOrganizationInvitationResponse): UserOrganizationInvitation {
        return new UserOrganizationInvitation(item);
      },
      acceptForSelectedInvitation: flow(function* acceptForSelectedInvitation(
        invitationId: number
      ) {
        const invitationPatch: singleItemPatch = {
          op: "replace",
          path: "/Status",
          value: OrganizationInvitationStatuses.Accepted,
        };
        const endpoint = endpoints.get("collaboration-status");
        endpoint.routeParams = [invitationId];
        const result = yield getEnv(self).api.patch(endpoint, [invitationPatch]);
        self.deselectAll();
        yield self.load();
        return result;
      }),
      rejectForSelectedInvitation: flow(function* rejectForSelectedInvitation(
        invitationId: number
      ) {
        const invitationPatch: singleItemPatch = {
          op: "replace",
          path: "/Status",
          value: OrganizationInvitationStatuses.Rejected,
        };
        const endpoint = endpoints.get("collaboration-status");
        endpoint.routeParams = [invitationId];
        const result = yield getEnv(self).api.patch(endpoint, [invitationPatch]);
        self.deselectAll();
        yield self.load();
        return result;
      }),
      afterCreate() {
        self.defaultSort = { field: "created", sort: "desc" };
        self.gridColumns = self.tableColumns;
        initializeInvitationStatusOptions();
      },
      setUserRoleOptions(userRoles: Role[]) {
        const userRolesOptions = userRoles.map((role) => {
          return {
            label: role.translationKey,
            value: role.id.toString(),
          };
        });
        self.userRoleOptions = cast(userRolesOptions);
      },
      setEmploymentStatusOptions(employmentStatuses: EmploymentStatusResponse[]) {
        const employmentStatusesOptions = employmentStatuses.map(
          (employmentStatus: EmploymentStatusResponse) => {
            return {
              label: `general.user.employment-status.${employmentStatus.name}`,
              value: employmentStatus.id.toString(),
            };
          }
        );
        self.employmentStatusOptions = cast(employmentStatusesOptions);
      },
    };
  })
  .views((self) => ({
    get selectedInvitation(): UserOrganizationInvitation | undefined {
      if (self.selectedItems.length <= 0 || !self.selectedItems[0]) return undefined;

      return self.data?.items.find(
        (e: UserOrganizationInvitation) => e.id.toString() === self.selectedItems[0]
      );
    },
  }))
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("collaboration_invitations");
      endpoint.queryParams = self.getDataQuery();
      return endpoint;
    },
    get idOfSelectedItem(): number {
      return self.selectedInvitation?.id ?? 0;
    },
    get isActionButtonDisabled() {
      if (!self.anyItemsSelected) return true;
      return !(self.selectedInvitation?.invitationStatus === OrganizationInvitationStatuses.New);
    },
    get selectedOrganizationName() {
      return self.selectedInvitation?.name ?? 0;
    },
  }));

export type MyOrganizationInvitationRequestsStorage = Instance<
  typeof MyOrganizationInvitationsStorageModel
>;
